import { Close } from '@mui/icons-material'
import { Box, Card, CircularProgress, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { getFormattedDate } from 'src/utils'
import { endpoints } from '../../../api/form-schema'
import { useGet } from '../../../api/reactQuery'
import ReportViewer from '../../../components/overallReport/ReportViewer'
import { IFilledFormSchemaReport, IFilledFormSchemaReportWithVersion } from '../../../generated-types/filled-form-schema-report'
import { FormRow } from '../../../generated-types/form-field'
import { ShowDialogProps } from '../../../show-dialog'

interface Props extends ShowDialogProps {
  report: IFilledFormSchemaReport
  customerId: EntityId
}

const getFileName = (url: string) => {
  const name = url.split('/').pop() || ''
  return name.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i, '')
}

const FormSchemaReportDetail: React.FC<Props> = ({ pop, report, customerId }) => {
  const { t: translate } = useTranslation(['common'])
  const { openFilePreviewDialog } = useDialogCtx()

  const { data: reportWithVersion, isLoading } = useGet<IFilledFormSchemaReportWithVersion>(
    endpoints.getFilledFormSchemaReportWithVersion(customerId, report.id),
  )

  const layouts: FormRow[] = reportWithVersion?.layout
    ? (() => {
      try {
        return JSON.parse(reportWithVersion.layout) as FormRow[]
      } catch {
        return []
      }
    })()
    : []

  return (
    <>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
        <Typography variant="h6">{report.formSchemaName ?? reportWithVersion?.formSchemaName}</Typography>
        <IconButton
          aria-label={translate('common:close')}
          onClick={pop}
          sx={{ color: 'inherit' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Metadata Section */}
          <Card sx={{ padding: 2, backgroundColor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>
              {translate('details', { ns: 'common' })}
            </Typography>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  {translate('form-schema', { ns: 'common' })}
                </Typography>
                <Typography variant="body1">{report.formSchemaName ?? reportWithVersion?.formSchemaName ?? '--'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  {translate('linked-entity', { ns: 'common' })}
                </Typography>
                <Typography variant="body1">
                  {report.linkedEntityName} ({translate(report.linkedEntityType, { ns: 'common' })})
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  {translate('locality', { ns: 'common' })}
                </Typography>
                <Typography variant="body1">{report.localityName ?? 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  {translate('filled-date', { ns: 'common' })}
                </Typography>
                <Typography variant="body1">
                  {report.filledDate ? getFormattedDate(new Date(report.filledDate)) : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  {translate('created-by', { ns: 'common' })}
                </Typography>
                <Typography variant="body1">{report.createdByUserName ?? reportWithVersion?.createdByUserName ?? '--'}</Typography>
              </Box>
            </Box>
          </Card>

          {/* Form Data Section via ReportViewer */}
          {isLoading ? (
            <Card sx={{ padding: 2, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Card>
          ) : reportWithVersion && layouts.length > 0 ? (
            <ReportViewer
              embedded
              layouts={layouts}
              schema={{
                name: reportWithVersion.formSchemaName,
                description: reportWithVersion.formSchemaDescription ?? '',
              }}
              report={{ values: reportWithVersion.values }}
              createdAt={reportWithVersion.filledDate}
              createdByUser={{ fullName: reportWithVersion.createdByUserName }}
            />
          ) : reportWithVersion && !layouts.length ? (
            <Card sx={{ padding: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {translate('no-data', { ns: 'common' })}
              </Typography>
            </Card>
          ) : !reportWithVersion && !isLoading ? (
            <Card sx={{ padding: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {translate('no-data', { ns: 'common' })}
              </Typography>
            </Card>
          ) : null}
        </Box>
      </DialogContent>
    </>
  )
}

export default FormSchemaReportDetail
